import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { X, GripVertical, Search, Eye, EyeOff, RotateCcw } from 'lucide-react';
import type { ColumnConfig } from '@/types';

// ═══════════════════════════════════════════
// Types
// ═══════════════════════════════════════════

interface ColumnInfo {
  field: string;
  type: string;
}

interface ColumnManagerProps {
  columns: ColumnInfo[];
  config: ColumnConfig[];
  onConfigChange: (config: ColumnConfig[]) => void;
  onClose: () => void;
}

// ═══════════════════════════════════════════
// Component
// ═══════════════════════════════════════════

export function ColumnManager({ columns, config, onConfigChange, onClose }: ColumnManagerProps) {
  const [localConfig, setLocalConfig] = useState<ColumnConfig[]>(() => [...config]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Clean up drag on mouseup anywhere
  useEffect(() => {
    const handler = () => setDragIndex(null);
    window.addEventListener('mouseup', handler);
    return () => window.removeEventListener('mouseup', handler);
  }, []);

  // Push changes to parent whenever localConfig changes
  useEffect(() => {
    onConfigChange(localConfig);
  }, [localConfig, onConfigChange]);

  // Build a type map from original columns for display
  const typeMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const col of columns) {
      map.set(col.field, col.type);
    }
    return map;
  }, [columns]);

  // Filtered indices for search (we show/hide rows but don't remove from array)
  const filteredIndices = useMemo(() => {
    if (!searchQuery.trim()) return localConfig.map((_, i) => i);
    const q = searchQuery.toLowerCase();
    return localConfig
      .map((cc, i) => (cc.field.toLowerCase().includes(q) ? i : -1))
      .filter((i) => i !== -1);
  }, [localConfig, searchQuery]);

  const visibleCount = localConfig.filter((cc) => cc.visible).length;

  const handleToggle = useCallback((index: number) => {
    setLocalConfig((prev) => {
      const next = [...prev];
      const item = next[index];
      // Prevent hiding the last visible column
      if (item.visible && visibleCount <= 1) return prev;
      next[index] = { ...item, visible: !item.visible };
      return next;
    });
  }, [visibleCount]);

  const handleShowAll = useCallback(() => {
    setLocalConfig((prev) => prev.map((cc) => ({ ...cc, visible: true })));
  }, []);

  const handleHideAll = useCallback(() => {
    // Keep at least the first column visible
    setLocalConfig((prev) =>
      prev.map((cc, i) => ({ ...cc, visible: i === 0 })),
    );
  }, []);

  const handleReset = useCallback(() => {
    setLocalConfig(columns.map((col) => ({ field: col.field, visible: true })));
  }, [columns]);

  const handleDragEnter = useCallback(
    (targetIndex: number) => {
      if (dragIndex === null || dragIndex === targetIndex) return;
      setLocalConfig((prev) => {
        const next = [...prev];
        const [removed] = next.splice(dragIndex, 1);
        next.splice(targetIndex, 0, removed);
        return next;
      });
      setDragIndex(targetIndex);
    },
    [dragIndex],
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-[420px] flex flex-col bg-dbgo-modal border rounded-lg shadow-2xl animate-fade-in"
        style={{ maxHeight: '500px' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <h2 className="text-sm font-semibold text-text-primary">Columns</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* Search + Actions */}
        <div className="px-4 pb-2 flex flex-col gap-2">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search columns..."
              className="w-full bg-dbgo-input border rounded-sm pl-7 pr-2 py-[5px] text-[12px] text-text-primary placeholder:text-text-muted outline-none focus:border-focus transition-colors"
              autoFocus
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleShowAll}
              className="flex items-center gap-1 px-2 py-1 text-[11px] text-text-secondary hover:text-text-primary rounded-sm hover:bg-dbgo-tab-hover transition-colors"
            >
              <Eye size={11} />
              Show All
            </button>
            <button
              onClick={handleHideAll}
              className="flex items-center gap-1 px-2 py-1 text-[11px] text-text-secondary hover:text-text-primary rounded-sm hover:bg-dbgo-tab-hover transition-colors"
            >
              <EyeOff size={11} />
              Hide All
            </button>
            <button
              onClick={handleReset}
              className="flex items-center gap-1 px-2 py-1 text-[11px] text-text-secondary hover:text-text-primary rounded-sm hover:bg-dbgo-tab-hover transition-colors"
            >
              <RotateCcw size={11} />
              Reset
            </button>
          </div>
        </div>

        {/* Column List */}
        <div
          ref={listRef}
          className="flex-1 min-h-0 overflow-y-auto border-t border-b px-1"
        >
          {filteredIndices.map((idx) => {
            const cc = localConfig[idx];
            const colType = typeMap.get(cc.field) ?? '';
            return (
              <div
                key={cc.field}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-sm transition-colors ${
                  dragIndex === idx ? 'bg-accent-blue/10' : 'hover:bg-dbgo-tab-hover'
                } ${!cc.visible ? 'opacity-50' : ''}`}
                onMouseEnter={() => handleDragEnter(idx)}
              >
                {/* Drag handle */}
                <span
                  className="flex-shrink-0 cursor-grab text-text-muted hover:text-text-secondary active:cursor-grabbing select-none"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setDragIndex(idx);
                  }}
                >
                  <GripVertical size={14} />
                </span>

                {/* Visibility checkbox */}
                <button
                  type="button"
                  onClick={() => handleToggle(idx)}
                  className={`w-4 h-4 flex-shrink-0 flex items-center justify-center rounded-sm border transition-colors ${
                    cc.visible
                      ? 'bg-accent-blue border-accent-blue text-white'
                      : 'border-[var(--border-default)] hover:border-accent-blue'
                  }`}
                >
                  {cc.visible && (
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M2 5L4.2 7.2L8 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>

                {/* Column name */}
                <span className="flex-1 min-w-0 text-xs text-text-primary truncate">
                  {cc.field}
                </span>

                {/* Column type badge */}
                {colType && (
                  <span className="flex-shrink-0 text-[10px] text-text-muted font-mono bg-dbgo-input px-1.5 py-0.5 rounded">
                    {colType}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-2.5">
          <span className="text-[11px] text-text-muted">
            {visibleCount} of {localConfig.length} columns visible
          </span>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-[5px] text-xs font-medium text-white bg-accent-blue hover:bg-accent-blue/90 rounded-sm transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
